import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useDailyRegistrations } from '@/hooks/use-daily-registrations';
import { CalendarIcon, Clock, Package } from 'lucide-react';
import { cn } from '@/lib/utils';

const DailyRegistrations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    return dateParam ? new Date(dateParam) : new Date();
  });

  const { registrations, isLoading, error, count } = useDailyRegistrations(selectedDate);

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const dateString = format(date, 'yyyy-MM-dd');
      setSearchParams({ date: dateString });
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ostomy':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'medicine':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      <main className="page-container">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="page-title">Daily Registrations</h1>
            <p className="text-gray-600 dark:text-gray-400">
              View usage records for {format(selectedDate, 'MMMM d, yyyy')}
            </p>
          </div>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[240px] justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateChange}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Usage Records ({count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading registrations...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600 dark:text-red-400">
                <p>Error loading registrations: {error}</p>
              </div>
            ) : registrations.length === 0 ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No registrations found for this date.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {registrations.map((registration) => (
                  <div
                    key={registration.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {format(registration.date, 'HH:mm')}
                          </span>
                        </div>
                        <Badge variant={registration.type === 'group' ? 'default' : 'secondary'}>
                          {registration.type === 'group' ? 'Group Usage' : 'Individual Usage'}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {registration.totalProducts} product{registration.totalProducts > 1 ? 's' : ''} • {registration.totalQuantity} total
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {registration.records.map((record, index) => (
                        <div key={record.id} className="flex items-center justify-between pl-4">
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                              {record.productName}
                            </h4>
                            <Badge className={getCategoryColor(record.productCategory)}>
                              {record.productCategory}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <div>Qty: {record.quantity}</div>
                            {record.outputVolume && (
                              <div>Output: {record.outputVolume}ml</div>
                            )}
                            {record.consistency && (
                              <div>Consistency: {record.consistency}</div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {registration.records[0].notes && (
                        <p className="mt-2 pl-4 text-sm text-gray-600 dark:text-gray-400">
                          Notes: {registration.records[0].notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DailyRegistrations;